import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { useToast } from './ui/Toast';
import './ChangelogManagement.css';
import {
  Sparkles, Plus, Edit, Trash2, Eye, EyeOff, X,
  Zap, Wrench, Rocket, AlertCircle
} from 'lucide-react';

// Admin CRUD for the "What's New" feed merchants see in their dashboard
// header (webapp) and More tab (mobile). Pure CRUD — no scheduling, no
// audience targeting yet (all merchants get every published entry).

const CATEGORY_CONFIG = {
  FEATURE:     { label: 'New Feature',  icon: Rocket, color: '#0d7463' },
  IMPROVEMENT: { label: 'Improvement',  icon: Zap,    color: '#2563eb' },
  FIX:         { label: 'Fix',          icon: Wrench, color: '#7c3aed' },
};

const STATUS_FILTERS = [
  { value: 'all',       label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'draft',     label: 'Drafts' },
];

const ChangelogManagement = () => {
  const toast = useToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyForm = {
    title: '',
    summary: '',
    body: '',
    category: 'FEATURE',
    isPublished: true,
    publishedAt: ''
  };
  const [form, setForm] = useState(emptyForm);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/changelog?status=${statusFilter}`);
      setEntries(response.entries || []);
    } catch (err) {
      console.error('Error fetching changelog:', err);
      toast.error('Failed to load changelog');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setForm({
      title: entry.title || '',
      summary: entry.summary || '',
      body: entry.body || '',
      category: entry.category || 'FEATURE',
      isPublished: !!entry.isPublished,
      publishedAt: entry.publishedAt ? entry.publishedAt.slice(0, 10) : ''
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSave = async () => {
    if (!form.title.trim() || !form.summary.trim()) {
      toast.error('Title and summary are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        summary: form.summary.trim(),
        body: form.body.trim() || null,
        category: form.category,
        isPublished: form.isPublished,
        publishedAt: form.publishedAt || null
      };
      if (editing) {
        await api.put(`/admin/changelog/${editing.id}`, payload);
        toast.success('Entry updated');
      } else {
        await api.post('/admin/changelog', payload);
        toast.success('Entry created');
      }
      closeModal();
      fetchEntries();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.put(`/admin/changelog/${id}/toggle`);
      fetchEntries();
    } catch (err) {
      toast.error('Failed to toggle publish state');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this changelog entry? Merchants will stop seeing it.')) return;
    try {
      await api.delete(`/admin/changelog/${id}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) {
      toast.error('Failed to delete entry');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="cl-page">
      <div className="cl-header">
        <div>
          <h1 className="cl-title"><Sparkles size={22} /> What's New</h1>
          <p className="cl-subtitle">Publish dated product updates that merchants see in the app.</p>
        </div>
        <button type="button" className="cl-btn cl-btn-primary" onClick={openCreate}>
          <Plus size={16} /> New entry
        </button>
      </div>

      <div className="cl-filters">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`cl-filter ${statusFilter === f.value ? 'is-active' : ''}`}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="cl-empty">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="cl-empty">
          <AlertCircle size={32} />
          <p>No changelog entries yet. Click "New entry" to publish your first update.</p>
        </div>
      ) : (
        <ul className="cl-list">
          {entries.map((e) => {
            const cfg = CATEGORY_CONFIG[e.category] || CATEGORY_CONFIG.FEATURE;
            const Icon = cfg.icon;
            return (
              <li key={e.id} className="cl-row">
                <div className="cl-row-main">
                  <span className="cl-cat-pill" style={{ background: cfg.color }}>
                    <Icon size={12} /> {cfg.label}
                  </span>
                  <div className="cl-row-content">
                    <div className="cl-row-title">{e.title}</div>
                    <div className="cl-row-summary">{e.summary}</div>
                    <div className="cl-row-meta">
                      <span>{e.isPublished ? `Published ${formatDate(e.publishedAt)}` : 'Draft'}</span>
                      <span>·</span>
                      <span>By {e.createdBy}</span>
                    </div>
                  </div>
                </div>
                <div className="cl-row-actions">
                  <button type="button" className="cl-icon-btn" onClick={() => handleToggle(e.id)} title={e.isPublished ? 'Unpublish' : 'Publish'}>
                    {e.isPublished ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button type="button" className="cl-icon-btn" onClick={() => openEdit(e)} title="Edit">
                    <Edit size={16} />
                  </button>
                  <button type="button" className="cl-icon-btn cl-icon-btn-danger" onClick={() => handleDelete(e.id)} title="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showModal && (
        <div className="cl-modal-overlay" onClick={closeModal}>
          <div className="cl-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="cl-modal-header">
              <h2>{editing ? 'Edit entry' : 'New entry'}</h2>
              <button type="button" className="cl-icon-btn" onClick={closeModal} aria-label="Close"><X size={18} /></button>
            </div>

            <div className="cl-modal-body">
              <label className="cl-field">
                <span className="cl-field-label">Title</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Faster payouts"
                />
              </label>

              <label className="cl-field">
                <span className="cl-field-label">Summary</span>
                <textarea
                  rows={2}
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  placeholder="One-line description merchants see in the feed"
                />
              </label>

              <label className="cl-field">
                <span className="cl-field-label">Details (optional, markdown)</span>
                <textarea
                  rows={5}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="Expanded notes, shown when merchants tap into the entry"
                />
              </label>

              <div className="cl-field-row">
                <label className="cl-field">
                  <span className="cl-field-label">Category</span>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                      <option key={key} value={key}>{cfg.label}</option>
                    ))}
                  </select>
                </label>

                <label className="cl-field">
                  <span className="cl-field-label">Publish date</span>
                  <input
                    type="date"
                    value={form.publishedAt}
                    onChange={(e) => setForm({ ...form, publishedAt: e.target.value })}
                  />
                </label>
              </div>

              <label className="cl-checkbox">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
                />
                <span>Publish immediately (merchants will see it)</span>
              </label>
            </div>

            <div className="cl-modal-footer">
              <button type="button" className="cl-btn cl-btn-ghost" onClick={closeModal}>Cancel</button>
              <button type="button" className="cl-btn cl-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create entry')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangelogManagement;
